import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Building2, ShieldCheck, ScrollText, Handshake, Info, Send } from "lucide-react";
import api, { apiError } from "../lib/apiClient";
import { Seo } from "../components/Seo";
import { SITE, AFFILIATE_DISCLOSURE } from "../lib/siteConfig";

function PageShell({ icon: Icon, title, subtitle, seoDescription, children, testid }) {
  return (
    <div className="max-w-3xl mx-auto fade-up" data-testid={testid}>
      <Seo title={title} description={seoDescription || subtitle} />
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-blush text-brand flex items-center justify-center"><Icon size={24} /></div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink">{title}</h1>
      </div>
      {subtitle && <p className="text-ink-soft mb-8">{subtitle}</p>}
      <div className="bg-white rounded-3xl border border-line shadow-soft p-6 md:p-9 space-y-5 text-ink-soft leading-relaxed">
        {children}
      </div>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-heading font-extrabold text-xl text-ink pt-2">{children}</h2>;

export function About() {
  return (
    <PageShell icon={Building2} title="About SIBLINGO" subtitle="Coordinated family shopping, made for our communities." testid="about-page">
      <p data-testid="about-intro">
        Siblingo helps families plan coordinated looks for several children, with a clear budget and optional accessories. Explore the labeled demos while we prepare our first merchant collection.
      </p>
      <p>
        Our initial focus is Orthodox Jewish families, including Hasidic communities, shopping for several children at once —
        modest styles, everyday clothing, and coordinated outfits for Shabbos, Yom Tov and special occasions.
      </p>
      <p data-testid="about-sizing">
        Official size charts are optional guidance. Compare the published values with measurements you provide and choose each size yourself. We do not predict fit or convert sizes between brands.
      </p>
      <H>Who we are</H>
      <p data-testid="about-operator">
        Siblingo is operated by <strong className="text-ink">{SITE.legalName}</strong>. We're inviting our first retail partners and
        will display their products within the permissions they provide.
      </p>
      <H>Get in touch</H>
      <p className="flex items-center gap-2"><Mail size={16} className="text-brand" /> <a className="hover:text-brand" href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
      <p className="flex items-start gap-2"><MapPin size={16} className="text-brand mt-0.5" /> {SITE.address}</p>
    </PageShell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const validate = (f) => {
    const e = {};
    if (!f.name.trim()) e.name = "Please enter your name.";
    if (!f.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
    if (!f.message.trim()) e.message = "Please enter a message.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const eMap = validate(form);
    setErrors(eMap);
    if (Object.keys(eMap).length) {
      const first = ["name", "email", "message"].find((k) => eMap[k]);
      const el = document.getElementById(`contact-${first}`);
      if (el) el.focus();
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we've received your message.");
      setForm({ name: "", email: "", message: "" });
      setErrors({});
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const clearErr = (k) => setErrors((p) => { if (!p[k]) return p; const n = { ...p }; delete n[k]; return n; });

  return (
    <PageShell icon={Mail} title="Contact Us" subtitle="Questions, feedback, or an affiliate inquiry? We'd love to hear from you." testid="contact-page">
      <p className="flex items-center gap-2"><Mail size={16} className="text-brand" /> <a className="hover:text-brand font-semibold text-ink" href={`mailto:${SITE.email}`} data-testid="contact-email">{SITE.email}</a></p>
      <p className="flex items-start gap-2"><MapPin size={16} className="text-brand mt-0.5" /> {SITE.address}</p>

      <form onSubmit={submit} noValidate className="pt-2 space-y-4" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Your name</label>
            <input
              id="contact-name"
              name="name"
              autoComplete="name"
              data-testid="contact-name-input"
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); clearErr("name"); }}
              required
              aria-required="true"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "contact-name-error" : undefined}
              className={`mt-1 w-full h-12 px-4 rounded-2xl border-2 bg-cream outline-none transition-all ${errors.name ? "border-red-500 focus:border-red-500" : "border-line focus:border-brand"}`}
              placeholder="Full name"
            />
            {errors.name && <p id="contact-name-error" role="alert" data-testid="contact-name-error" className="mt-1 text-xs font-semibold text-red-600">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="contact-email" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Email</label>
            <input
              id="contact-email"
              name="email"
              autoComplete="email"
              data-testid="contact-email-input"
              type="email"
              value={form.email}
              onChange={(e) => { setForm({ ...form, email: e.target.value }); clearErr("email"); }}
              required
              aria-required="true"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "contact-email-error" : undefined}
              className={`mt-1 w-full h-12 px-4 rounded-2xl border-2 bg-cream outline-none transition-all ${errors.email ? "border-red-500 focus:border-red-500" : "border-line focus:border-brand"}`}
              placeholder="you@example.com"
            />
            {errors.email && <p id="contact-email-error" role="alert" data-testid="contact-email-error" className="mt-1 text-xs font-semibold text-red-600">{errors.email}</p>}
          </div>
        </div>
        <div>
          <label htmlFor="contact-message" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Message</label>
          <textarea
            id="contact-message"
            name="message"
            data-testid="contact-message-input"
            value={form.message}
            onChange={(e) => { setForm({ ...form, message: e.target.value }); clearErr("message"); }}
            rows={5}
            required
            aria-required="true"
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? "contact-message-error" : undefined}
            className={`mt-1 w-full p-4 rounded-2xl border-2 bg-cream outline-none transition-all resize-none ${errors.message ? "border-red-500 focus:border-red-500" : "border-line focus:border-brand"}`}
            placeholder="How can we help?"
          />
          {errors.message && <p id="contact-message-error" role="alert" data-testid="contact-message-error" className="mt-1 text-xs font-semibold text-red-600">{errors.message}</p>}
        </div>
        <button
          type="submit"
          disabled={sending}
          data-testid="contact-submit-btn"
          className="w-full sm:w-auto h-12 px-7 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover active:scale-95 transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {sending ? "Sending…" : <>Send message <Send size={16} /></>}
        </button>
      </form>
    </PageShell>
  );
}

export function Privacy() {
  return (
    <PageShell icon={ShieldCheck} title="Privacy Policy" subtitle="Last updated September 16, 2026" seoDescription="How SIBLINGO handles family profiles, children's sizing details, saved searches and contact messages — what we collect, how it's used, and how to request deletion." testid="privacy-page">
      <p><strong className="text-ink">{SITE.legalName}</strong> ("SIBLINGO", "we", "us") respects your privacy. This policy explains what we collect and how we use it.</p>
      <H>Information we collect</H>
      <p>We collect the information you provide when you create an account (such as your name and email address), the family profile details you choose to add (each child's nickname, date of birth, gender/type, and sizing information — including height, weight, body measurements such as chest, waist and inseam, and known brand sizes and fit preferences), an optional family label and location, your saved searches and their criteria, your deal alerts and notification contacts, and any messages you send us. We also collect basic technical data such as your device type and pages visited, and we store your sign-in token in your browser to keep you logged in.</p>
      <p>Sizing values you enter are stored as you provide them. Saved official chart references stay separate from your child's measurements. Previously saved estimates remain labeled; they are not used to predict sizes or fill in missing measurements. You can review, update or remove your family profile details at any time.</p>
      <H>How we use your information</H>
      <p>We use your information to operate SIBLINGO, match deals to your preferences, send the alerts you request, respond to your messages, and improve our service. We never sell your personal information.</p>
      <H>Cookies</H>
      <p>We use cookies and similar local storage to keep you signed in and remember your preferences. You can control cookies through your browser settings, though some features may not work without them.</p>
      <H>Affiliate links</H>
      <p>{AFFILIATE_DISCLOSURE} Eligible retailer links are labeled ‘Affiliate link’; links without that label are ordinary retailer links. When you click through, the retailer may set its own cookies under its own privacy policy.</p>
      <H>Partner (affiliate/contributor) data</H>
      <p>If you are an approved partner, we additionally collect and use data specific to the partner program: your partner status and permissions, your agreed commission percentage, the deal and product information you submit (titles, prices, sizes, retailer links and affiliate tags), the moderation/approval status of your submissions, and — for deals you own — the SIBLINGO detail-page views, favorites/saves, and outbound retailer clicks attributed to them, along with earnings, affiliate-revenue and payment records. This data is used to operate the partner program, calculate commissions, and prevent abuse.</p>
      <p>Attribution is limited to what retailers and affiliate networks actually report back to SIBLINGO; some conversions and revenue may never be reported and are shown as "Not tracked." Each partner can see statistics and earnings only for deals they own; partners can never see another partner's data. Consumer child, family, sizing or search data is never exposed to partners in any statistics. Partner ownership and earnings records are retained even after partner access is turned off.</p>
      <H>Your rights</H>
      <p>You may access or correct your account information at any time from your account settings. To request deletion of your account and associated information, email us at <a className="text-brand hover:underline" href={`mailto:${SITE.email}`}>{SITE.email}</a> and we will process your request. You may unsubscribe from alerts whenever you like.</p>
      <H>Contact</H>
      <p className="flex items-center gap-2"><Mail size={16} className="text-brand" /> <a className="hover:text-brand" href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
    </PageShell>
  );
}

export function Terms() {
  return (
    <PageShell icon={ScrollText} title="Terms of Use" subtitle="Last updated September 16, 2026" seoDescription="The terms for using SIBLINGO: illustrative pre-launch content, optional official chart guidance and parent-selected sizes, affiliate links, partner submissions and membership rules." testid="terms-page">
      <p>By using SIBLINGO, operated by <strong className="text-ink">{SITE.legalName}</strong>, you agree to these Terms of Use.</p>
      <H>Use of the service</H>
      <p>SIBLINGO is a deals discovery platform. We surface sales and matching sets from stores and approved partners. Ordinary members agree to use SIBLINGO only for lawful, personal, non-commercial purposes.</p>
      <H>Authorized partner use</H>
      <p>Separately, SIBLINGO may grant approved affiliate/contributor partners the ability to submit deals and product listings and to earn commission. This authorized-partner use is commercial and is governed by the following, in addition to the rest of these Terms:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong className="text-ink">Submissions & approval.</strong> Partners may submit deals/listings for review. Nothing is public until SIBLINGO approves it. SIBLINGO may approve, reject, edit, hold or remove any submission at its discretion.</li>
        <li><strong className="text-ink">Affiliate links & commissions.</strong> Approved partners may have affiliate/tracking links associated with their deals and may earn a commission, defined as the partner's agreed percentage of the affiliate revenue SIBLINGO actually receives — not a share of product price, retailer sales, clicks or gross merchandise value.</li>
        <li><strong className="text-ink">Accuracy duties.</strong> Partners must submit only accurate, current, in-stock information with truthful prices, sizes and images, and must not upload images containing people or otherwise immodest content.</li>
        <li><strong className="text-ink">Content rights.</strong> Partners grant SIBLINGO a license to display submitted content and confirm they have the right to submit it.</li>
        <li><strong className="text-ink">Prohibited conduct.</strong> No misrepresentation, cloaking, self-clicking, incentivized clicks, or attempts to access another partner's data or statistics.</li>
        <li><strong className="text-ink">Removal & suspension.</strong> SIBLINGO may remove listings and suspend or revoke partner access at any time, including for inaccuracy or policy violations. Historical ownership and earnings records are retained.</li>
        <li><strong className="text-ink">Payout limitations.</strong> Commissions are payable only on affiliate revenue actually received and confirmed by SIBLINGO, subject to retailer/network attribution, reversals, minimum thresholds and applicable holdbacks. Unverified or reversed amounts are not payable.</li>
      </ul>
      <H>No purchases through SIBLINGO</H>
      <p>SIBLINGO does not sell products or process payments for third-party deals. When you buy, you purchase directly from the retailer, subject to that retailer's own terms, pricing, availability and return policies.</p>
      <H>Deal accuracy</H>
      <p>We work to keep deals accurate and current, but prices, discounts and availability change frequently and are set by the retailers. We do not guarantee that any deal will be available or priced as shown.</p>
      <H>Affiliate links</H>
      <p>Eligible retailer links are labeled ‘Affiliate link.’ We may earn a commission if you purchase through them; the price you pay is set by the retailer and is never higher because of our link.</p>
      <H>Accounts</H>
      <p>You are responsible for keeping your account credentials secure and for activity under your account. Premium features may require a paid subscription as described at checkout.</p>
      <H>Changes</H>
      <p>We may update these terms from time to time. Continued use of SIBLINGO after changes means you accept the updated terms.</p>
      <H>Contact</H>
      <p className="flex items-center gap-2"><Mail size={16} className="text-brand" /> <a className="hover:text-brand" href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
    </PageShell>
  );
}

export function NotFound() {
  return (
    <PageShell icon={Info} title="Page not found" subtitle="That address doesn't exist on SIBLINGO." testid="not-found-page">
      <Seo title="Page not found" description="This page could not be found on SIBLINGO." robots="noindex,follow" canonical={false} />
      <p>The link may be out of date or mistyped. Nothing here was purchased, saved or changed.</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><a className="font-bold text-brand underline" href="/family-deals" data-testid="not-found-collections-link">Browse Curated Family Sets</a></li>
        <li><a className="font-bold text-brand underline" href="/" data-testid="not-found-home-link">Go to the SIBLINGO home page</a></li>
        <li><a className="font-bold text-brand underline" href="/contact" data-testid="not-found-contact-link">Contact us</a> if you followed a link from our site</li>
      </ul>
    </PageShell>
  );
}

export function AffiliateDisclosurePage() {
  return (
    <PageShell icon={Handshake} title="Affiliate Disclosure" seoDescription="SIBLINGO may earn a commission when you buy through labeled retailer links. Browsing, sizing assistance and saved searches are free; prices never change because of our links." testid="affiliate-disclosure-page">
      <div className="flex items-start gap-2 bg-blush border border-brand/15 rounded-2xl p-4 text-ink">
        <Info size={18} className="text-brand shrink-0 mt-0.5" />
        <span className="font-semibold">{AFFILIATE_DISCLOSURE}</span>
      </div>
      <p>SIBLINGO, operated by <strong className="text-ink">{SITE.legalName}</strong>, may take part in retailer affiliate programs.</p>
      <p data-testid="affiliate-marking-statement">Eligible retailer links are labeled ‘Affiliate link.’ We may earn a commission if you purchase through them. Links without that label are ordinary retailer links.</p>
      <H>What this means for you</H>
      <p>The price you pay is set by the retailer and is never higher because of our link; retailer prices and offers may change. Browsing, sizing assistance and saved searches are free; optional paid features, when available, are described before purchase.</p>
      <H>Our promise</H>
      <p>We feature deals we believe offer genuine value to our community. Any commission we may earn never changes our commitment to surfacing honest, useful deals.</p>
      <H>Questions?</H>
      <p className="flex items-center gap-2"><Mail size={16} className="text-brand" /> <a className="hover:text-brand" href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
    </PageShell>
  );
}
